import { useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useAdminApiClient } from '../api/AdminApiClientContext'
import { queryKeys, type LifecycleTab } from '../api/queryKeys'
import type {
  DormantAccountListResponse,
  DormantAccountRow,
  NoVerifiedEmailListResponse,
  NoVerifiedEmailRow,
  PendingDeletionListResponse,
  PendingDeletionRow,
} from '../api/types'
import { CodeChip } from '../components/CodeChip'
import {
  DataTable, type ColumnConfig,
} from '../components/DataTable'
import { PaginationControl } from '../components/PaginationControl'
import { RelativeTime } from '../components/RelativeTime'
import { useCursorPaginatedQuery } from '../hooks/useCursorPaginatedQuery'

// plan-admin/09 — Account Lifecycle queue.
//
// Surfaces the three flavours of "this account needs a human
// decision but is not in the death-confirmation queue":
//
//   1. Dormant — no recent meaningful activity.
//   2. No verified email — life-check started but no verified
//      notification email; reuses the Plan-03 follow-up flag.
//   3. Pending deletion — user hit the Flutter "delete my account"
//      flow and we are waiting on operator review for any in-flight
//      deliveries before the cleanup proceeds.
//
// All three lists are read-only (the actual cleanup actions are out
// of scope for plan-09; tracked in plan-10 Future Considerations).
// Tab state lives in the URL (?tab=...) so a deep link or refresh
// keeps the operator on the same view.

const PAGE_SIZE = 50

const TABS: readonly { key: LifecycleTab; label: string }[] = [
  { key: 'dormant', label: 'Dormant' },
  { key: 'no-verified-email', label: 'No verified email' },
  { key: 'pending-deletion', label: 'Pending deletion' },
]

function readTab(params: URLSearchParams): LifecycleTab {
  const raw = params.get('tab') ?? ''
  if (TABS.some((t) => t.key === raw)) {
    return raw as LifecycleTab
  }
  return 'dormant'
}

export function AccountLifecyclePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = useMemo(() => readTab(searchParams), [searchParams])

  function selectTab(tab: LifecycleTab) {
    const next = new URLSearchParams(searchParams)
    if (tab === 'dormant') {
      next.delete('tab')
    } else {
      next.set('tab', tab)
    }
    setSearchParams(next, { replace: true })
  }

  return (
    <div className="space-y-4 p-6">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-xl font-semibold text-stone-900">
            Account lifecycle
          </h1>
          <p className="mt-1 text-sm text-stone-500">
            Accounts needing a lifecycle decision outside the
            death-confirmation queue. Read-only — the cleanup
            actions land in a future plan.
          </p>
        </div>
      </header>

      <div role="tablist" aria-label="Account lifecycle tabs"
           className="flex gap-1 border-b border-stone-200">
        {TABS.map((tab) => {
          const selected = tab.key === activeTab
          return (
            <button
              key={tab.key}
              role="tab"
              type="button"
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              onClick={() => selectTab(tab.key)}
              className={[
                'px-4 py-2 text-sm font-medium',
                selected
                  ? 'border-b-2 border-accent text-accent'
                  : 'text-stone-600 hover:text-stone-900',
              ].join(' ')}
            >
              {tab.label}
            </button>
          )
        })}
      </div>

      {activeTab === 'dormant' && <DormantTab />}
      {activeTab === 'no-verified-email' && <NoVerifiedEmailTab />}
      {activeTab === 'pending-deletion' && <PendingDeletionTab />}
    </div>
  )
}

// --- Dormant -----------------------------------------------------

function DormantTab() {
  const client = useAdminApiClient()
  const navigate = useNavigate()
  const result = useCursorPaginatedQuery<DormantAccountRow>({
    queryKey: queryKeys.lifecycle('dormant'),
    fetchPage: (cursor) => {
      const search = new URLSearchParams({ limit: String(PAGE_SIZE) })
      if (cursor) search.set('cursor', cursor)
      return client.get<DormantAccountListResponse>(
        `/r/admin/lifecycle/dormant?${search.toString()}`,
      )
    },
  })

  const rows = result.flatItems
  const columns: ColumnConfig<DormantAccountRow>[] = [
    {
      key: 'link_account',
      header: 'Link account',
      cell: (row) => <CodeChip>{row.link_account}</CodeChip>,
    },
    {
      key: 'display_name',
      header: 'Display name',
      cell: (row) => row.display_name
        || <span className="text-stone-400">—</span>,
    },
    {
      key: 'dormant_for_days',
      header: 'Dormant for',
      align: 'right',
      cell: (row) => `${row.dormant_for_days}d`,
    },
    {
      key: 'last_replied',
      header: 'Last replied',
      cell: (row) => row.last_replied
        ? <RelativeTime timestamp={row.last_replied} />
        : <span className="text-xs text-stone-400">never</span>,
    },
    {
      key: 'activated_message_count',
      header: 'Active msgs',
      align: 'right',
      cell: (row) => row.activated_message_count,
    },
    {
      key: 'verified_notification_email_count',
      header: 'Verified emails',
      align: 'right',
      cell: (row) => row.verified_notification_email_count,
    },
  ]

  return (
    <DataTable<DormantAccountRow>
      caption="Dormant accounts"
      columns={columns}
      rows={rows}
      rowKey={(row) => row.link_account}
      isLoading={result.isLoading}
      isError={result.isError}
      errorMessage={result.error?.message ?? 'Failed to load dormant accounts.'}
      emptyTitle="No dormant accounts"
      emptyDescription="Users with no recent activity will appear here."
      onRowClick={(row) =>
        navigate(`/death-confirmation/${row.link_account}`)
      }
      footer={
        <PaginationControl
          loadedCount={rows.length}
          hasMore={result.hasMore}
          isFetchingMore={result.isFetchingMore}
          onLoadMore={() => result.loadMore()}
        />
      }
    />
  )
}

// --- No verified email -------------------------------------------

function NoVerifiedEmailTab() {
  const client = useAdminApiClient()
  const navigate = useNavigate()
  const result = useCursorPaginatedQuery<NoVerifiedEmailRow>({
    queryKey: queryKeys.lifecycle('no-verified-email'),
    fetchPage: (cursor) => {
      const search = new URLSearchParams({ limit: String(PAGE_SIZE) })
      if (cursor) search.set('cursor', cursor)
      return client.get<NoVerifiedEmailListResponse>(
        `/r/admin/lifecycle/no-verified-email?${search.toString()}`,
      )
    },
  })

  const rows = result.flatItems
  const columns: ColumnConfig<NoVerifiedEmailRow>[] = [
    {
      key: 'link_account',
      header: 'Link account',
      cell: (row) => <CodeChip>{row.link_account}</CodeChip>,
    },
    {
      key: 'display_name',
      header: 'Display name',
      cell: (row) => row.display_name
        || <span className="text-stone-400">—</span>,
    },
    {
      key: 'last_replied',
      header: 'Last replied',
      cell: (row) => row.last_replied
        ? <RelativeTime timestamp={row.last_replied} />
        : <span className="text-xs text-stone-400">never</span>,
    },
    {
      key: 'activated_message_count',
      header: 'Active msgs',
      align: 'right',
      cell: (row) => row.activated_message_count,
    },
    {
      key: 'verified_notification_email_count',
      header: 'Verified emails',
      align: 'right',
      cell: (row) => row.verified_notification_email_count,
    },
  ]

  return (
    <DataTable<NoVerifiedEmailRow>
      caption="Accounts blocked by missing verified email"
      columns={columns}
      rows={rows}
      rowKey={(row) => row.link_account}
      isLoading={result.isLoading}
      isError={result.isError}
      errorMessage={result.error?.message
        ?? 'Failed to load no-verified-email accounts.'}
      emptyTitle="No accounts blocked by missing email"
      emptyDescription="Users with life-check on but no verified email will appear here."
      onRowClick={(row) =>
        navigate(`/death-confirmation/${row.link_account}`)
      }
      footer={
        <PaginationControl
          loadedCount={rows.length}
          hasMore={result.hasMore}
          isFetchingMore={result.isFetchingMore}
          onLoadMore={() => result.loadMore()}
        />
      }
    />
  )
}

// --- Pending deletion --------------------------------------------

function PendingDeletionTab() {
  const client = useAdminApiClient()
  const navigate = useNavigate()
  const result = useCursorPaginatedQuery<PendingDeletionRow>({
    queryKey: queryKeys.lifecycle('pending-deletion'),
    fetchPage: (cursor) => {
      const search = new URLSearchParams({ limit: String(PAGE_SIZE) })
      if (cursor) search.set('cursor', cursor)
      return client.get<PendingDeletionListResponse>(
        `/r/admin/lifecycle/pending-deletion?${search.toString()}`,
      )
    },
  })

  const rows = result.flatItems
  const columns: ColumnConfig<PendingDeletionRow>[] = [
    {
      key: 'link_account',
      header: 'Link account',
      cell: (row) => <CodeChip>{row.link_account}</CodeChip>,
    },
    {
      key: 'display_name',
      header: 'Display name',
      cell: (row) => row.display_name
        || <span className="text-stone-400">—</span>,
    },
    {
      key: 'requested_time',
      header: 'Requested',
      cell: (row) => row.requested_time
        ? <RelativeTime timestamp={row.requested_time} />
        : <span className="text-xs text-stone-400">—</span>,
    },
    {
      key: 'activated_message_count',
      header: 'Active msgs',
      align: 'right',
      cell: (row) => row.activated_message_count,
    },
    {
      key: 'delivery_in_flight',
      header: 'Status',
      cell: (row) => row.delivery_in_flight
        ? (
          <span
            data-testid={`in-flight-${row.link_account}`}
            className="rounded-full bg-amber-100 px-2 py-0.5 text-xs
                       font-semibold text-amber-900"
          >
            In flight
          </span>
        )
        : (
          <span className="text-xs text-stone-500">Idle</span>
        ),
    },
  ]

  return (
    <DataTable<PendingDeletionRow>
      caption="Accounts the user has requested deletion for"
      columns={columns}
      rows={rows}
      rowKey={(row) => row.link_account}
      isLoading={result.isLoading}
      isError={result.isError}
      errorMessage={result.error?.message
        ?? 'Failed to load pending-deletion accounts.'}
      emptyTitle="No pending deletion requests"
      emptyDescription="Accounts the user requests deletion for will appear here."
      onRowClick={(row) =>
        navigate(`/death-confirmation/${row.link_account}`)
      }
      footer={
        <PaginationControl
          loadedCount={rows.length}
          hasMore={result.hasMore}
          isFetchingMore={result.isFetchingMore}
          onLoadMore={() => result.loadMore()}
        />
      }
    />
  )
}
